import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios'
import { authStore } from '@/store/authStore'
import { ApiError } from '@/types/comum'

/**
 * O que os serviços recebem no `catch`.
 *
 * Não é o `ApiError` do backend — é este envelope, montado pelo interceptor a partir da
 * resposta. Antes havia um cast direto para `ApiError`, que o compilador recusava por não
 * haver sobreposição suficiente: a forma era outra, e o cast escondia isso.
 */
export interface RequisicaoFalhou {
  message: string
  status?: number
  data?: ApiError
  /**
   * Id de correlação da requisição (header `X-Correlation-Id`, gerado no gateway). É o que
   * liga este erro às linhas de log de todos os serviços — em erro de servidor ele já vai
   * anexado à `message` como "ref.", para o usuário citar ao reportar.
   */
  correlationId?: string
}

/** Trecho curto do id para a mensagem — o completo fica em `correlationId`. */
const refCurta = (id: string) => id.length > 8 ? id.slice(0, 8) : id

/**
 * Cliente HTTP baseado em cookies HttpOnly.
 *
 * Tokens não são mais armazenados em JS. O navegador anexa
 * automaticamente os cookies de sessão a cada requisição quando
 * `withCredentials: true`. Em 401, chamamos /refresh (que também
 * funciona via cookie HttpOnly do refresh token).
 */
const api: AxiosInstance = axios.create({
  // Default RELATIVO (''): em dev o proxy do Vite (`/api` → :8080) resolve;
  // em prod a mesma origem passa pelo nginx do frontend (`/api` → gateway),
  // evitando CORS. Sobrescreva com VITE_API_BASE_URL só se a API ficar em
  // outra origem (ex.: domínio de API separado).
  baseURL: import.meta.env.VITE_API_BASE_URL ?? '',
  timeout: 30000,
  // Crítico: envia cookies cross-origin. Em conjunto com CORS
  // allowCredentials=true no Gateway, permite que o cookie HttpOnly
  // de sessão acompanhe cada chamada.
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
})

// Estado local para coordenar refresh concorrente — evita disparar N
// chamadas a /refresh quando várias requisições falham com 401 ao
// mesmo tempo.
let refreshInFlight: Promise<boolean> | null = null

async function tryRefresh(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight
  refreshInFlight = (async () => {
    try {
      const baseURL = import.meta.env.VITE_API_BASE_URL ?? ''
      // O refresh_token já flui via cookie HttpOnly — não enviamos no body.
      await axios.post(`${baseURL}/api/v1/auth/refresh`, {}, { withCredentials: true })
      return true
    } catch {
      authStore.getState().logout()
      return false
    } finally {
      refreshInFlight = null
    }
  })()
  return refreshInFlight
}

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => config,
  (error) => Promise.reject(error),
)

api.interceptors.response.use(
  (response) => response.data,
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }
    const url = originalRequest?.url ?? ''

    const isAuthEndpoint =
      url.includes('/auth/login') ||
      url.includes('/auth/refresh') ||
      url.includes('/auth/register') ||
      url.includes('/auth/logout')

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true
      const ok = await tryRefresh()
      if (ok) {
        return api(originalRequest)
      }
    }

    // Validação do backend vem como { message, errorCode: 'VALIDATION_ERROR', data: {campo: "mensagem"} }
    const body = error.response?.data as
      | (ApiError & { errorCode?: string; data?: Record<string, string> })
      | undefined
    const baseMessage = body?.message || error.message || 'Erro ao processar requisição'

    let errorMessage = baseMessage
    if (body?.errorCode === 'VALIDATION_ERROR' && body.data && typeof body.data === 'object') {
      const detalhes = Object.entries(body.data)
        .map(([campo, msg]) => `${campo}: ${msg}`)
        .join(' · ')
      if (detalhes) errorMessage = `${baseMessage} — ${detalhes}`
    }

    // Correlação: o corpo de erro traz `correlationId`; o header vem mesmo quando o corpo
    // não é nosso (gateway, timeout do proxy). Em erro de servidor a referência vai na
    // mensagem — é o que o usuário consegue citar; em erro de negócio (4xx) não ajuda.
    const correlationId =
      (body as { correlationId?: string } | undefined)?.correlationId ??
      (error.response?.headers?.['x-correlation-id'] as string | undefined)
    const status = error.response?.status
    if (correlationId && status != null && status >= 500) {
      errorMessage = `${errorMessage} (ref. ${refCurta(correlationId)})`
    }

    // Forma REAL do erro que chega aos serviços — não é o ApiError do backend, é este
    // envelope. Declarar assim evita o cast que mentia e deixa o autocompletar correto
    // em quem faz `catch (e)`.
    const falha: RequisicaoFalhou = {
      message: errorMessage,
      status,
      data: error.response?.data,
      correlationId,
    }
    return Promise.reject(falha)
  },
)

export default api
